import React, { Component } from "react";
import { Card, List, Tag, Statistic, Row, Col, Avatar, Typography } from "antd";
import {
  UserOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { JS_COOKIE } from "util/auth";
import { ADMIN_COOKIE_NAME } from "constants/admin";
import api_address from "constants/config";

const { Title, Text } = Typography;

interface CoachAssignmentViewProps {
  coachId: string;
}

interface CoachAssignmentViewState {
  assignments: Array<{
    id: number;
    link: string;
    comment: string;
    status: string;
    assigned_at: string;
    result_id: number;
    steps: any;
    currentstep: string;
    completed: boolean;
  }>;
  summary: {
    totalChildren: number;
    totalVideos: number;
    completedEvaluations: number;
    pendingEvaluations: number;
  };
  loading: boolean;
}

class CoachAssignmentView extends Component<
  CoachAssignmentViewProps,
  CoachAssignmentViewState
> {
  constructor(props: CoachAssignmentViewProps) {
    super(props);
    this.state = {
      assignments: [],
      summary: {
        totalChildren: 0,
        totalVideos: 0,
        completedEvaluations: 0,
        pendingEvaluations: 0,
      },
      loading: false,
    };
  }

  componentDidMount() {
    this.loadAssignments();
  }

  componentDidUpdate(prevProps: CoachAssignmentViewProps) {
    if (prevProps.coachId !== this.props.coachId) {
      this.loadAssignments();
    }
  }

  loadAssignments = async () => {
    this.setState({ loading: true });
    try {
      const response = await axios.get(
        `${api_address}api/admin/coach/${this.props.coachId}/assignments`,
        {
          headers: {
            Authorization: `Bearer ${JS_COOKIE.get(ADMIN_COOKIE_NAME)}`,
          },
        }
      );

      this.setState({
        assignments: response.data.assignments || [],
        summary: response.data.summary || {
          totalChildren: 0,
          totalVideos: 0,
          completedEvaluations: 0,
          pendingEvaluations: 0,
        },
      });
    } catch (error) {
      console.error("Error loading coach assignments:", error);
    } finally {
      this.setState({ loading: false });
    }
  };

  getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "finished":
        return "green";
      case "reviewing":
      case "pending":
        return "orange";
      case "waiting":
        return "blue";
      default:
        return "default";
    }
  };

  getStatusIcon = (completed: boolean, status: string) => {
    if (completed) {
      return <CheckCircleOutlined rev="" style={{ color: "#52c41a" }} />;
    }
    return <ClockCircleOutlined rev="" style={{ color: "#faad14" }} />;
  };

  render() {
    const { assignments, summary, loading } = this.state;

    return (
      <div>
        {/* Summary Statistics */}
        <Card title="Assignment Summary" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="Total Children"
                value={summary.totalChildren}
                prefix={<UserOutlined rev="" />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Completed"
                value={summary.completedEvaluations}
                prefix={<CheckCircleOutlined rev="" />}
                valueStyle={{ color: "#3f8600" }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Pending"
                value={summary.pendingEvaluations}
                prefix={<ClockCircleOutlined rev="" />}
                valueStyle={{ color: "#cf1322" }}
              />
            </Col>
          </Row>
        </Card>

        {/* Assignment List */}
        <Card title="Current Assignments" loading={loading}>
          <List
            dataSource={assignments}
            locale={{ emptyText: "No assignments found for this coach" }}
            renderItem={(assignment) => {
              const childName = assignment.childname || "Unknown Child";
              const statusColor = this.getStatusColor(assignment.status);

              return (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={<UserOutlined rev="" />}
                        style={{ backgroundColor: "#1890ff" }}
                      />
                    }
                    title={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Text strong>{childName}</Text>
                        {this.getStatusIcon(
                          assignment.completed,
                          assignment.status
                        )}
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>
                          <Text type="secondary">Result ID: </Text>
                          <Text code>{assignment.result_id}</Text>
                        </div>
                        <div style={{ marginBottom: 4 }}>
                          <Text type="secondary">Current Step: </Text>
                          <Tag color={statusColor}>
                            {assignment.currentstep}
                          </Tag>
                        </div>
                        <div style={{ marginBottom: 4 }}>
                          <Text type="secondary">Video Status: </Text>
                          <Tag color={statusColor}>{assignment.status}</Tag>
                        </div>
                        {assignment.comment && (
                          <div style={{ marginBottom: 4 }}>
                            <Text type="secondary">Comment: </Text>
                            <Text>{assignment.comment}</Text>
                          </div>
                        )}
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Assigned:{" "}
                            {new Date(assignment.created_at).toLocaleString()}
                          </Text>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </Card>
      </div>
    );
  }
}

export default CoachAssignmentView;
